import React, { useEffect, useState } from 'react';

import { PageLayout } from '@/components/templates/PageLayout';
import {
  CmBox,
  CmCard,
  CmFlex,
  CmHeading,
  CmLongLoading,
  CmScrollBox,
  CmTable,
  CmTableBodyRow,
  CmTableHeader,
  CmTbody,
  CmTd,
  CmTh,
  ExternalLinkIcon,
} from '@/components/uiParts';
import { DateDomainService } from '@/domain/services';
import { ProductInformationDetailModal } from '@/pages/home/components/Modal/ProductInformationDetailModal';
import { routes } from '@/router';
import {
  fetchNewsList,
  fetchProductInformationList,
} from '@/services/MicroCms/service';

/* 画像 */
import DemaecanImage from './image/demaecan.png';
import dokodemoChumonkunImage from './image/dokodemo-chumonkun.png';
import EpsonImage from './image/epson.png';
import FoodFrontiaImage from './image/food-frontia.png';
import MakasetenetexImage from './image/makasetenetex.png';
import MenuImage from './image/menu.png';
import NecMobileImage from './image/nec-pos.png';
import RepitteImage from './image/repitte.png';
import SmaregiImage from './image/smaregi.png';
import StarImage from './image/star.png';
import TakemeOrder from './image/takeme_order.png';
import TeraokaImage from './image/teraoka.png';
import UberEatsImage from './image/uber-eats.png';

export type News = {
  id: string;
  title: string;
  publishedAt: string;
  body: string;
  url: string;
  info_state: '' | InformationState;
  role: 'news' | 'productInformation';
};

export type InformationState =
  | 'function_addition'
  | 'functional_improvement'
  | 'information';

const HomePage: React.FC = () => {
  const [newsList, setNewsList] = useState<News[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [productInformation, setProductInformation] = useState<
    News | undefined
  >(undefined);
  const { formatCustom } = DateDomainService;
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const { contents: newsContent } = await fetchNewsList();
      const buildNewsContent = newsContent.map<News>((content) => {
        const { id, title, publishedAt, body, url } = content;
        return {
          id,
          title,
          publishedAt,
          body,
          url,
          role: 'news',
          info_state: '',
        };
      });
      const { contents } = await fetchProductInformationList();
      const buildContents = contents.map<News>((content) => {
        const { id, title, publishedAt, body, info_state } = content;
        return {
          id,
          title,
          publishedAt,
          body,
          url: '',
          info_state: info_state,
          role: 'productInformation',
        };
      });
      const allNews = [...buildNewsContent, ...buildContents];
      const sortedNewsList = allNews.sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime() // 降順
      );
      setNewsList(sortedNewsList);
      setIsLoading(false);
    })();
  }, []);

  return (
    <PageLayout title={routes.Home.title}>
      <CmFlex pt={'30px'} justifyContent="space-between">
        <CmBox width={`calc(100% - ${400 + 20}px )`}>
          <CmScrollBox height={`calc(100vh - 110px)`}>
            <CmBox>
              <CmHeading mb="20px">お知らせ・更新情報</CmHeading>
              <CmCard>
                <CmTable>
                  <CmTableHeader>
                    <CmTh textAlign={'left'} minWidth={'100px'}>
                      掲載日
                    </CmTh>
                    <CmTh textAlign={'left'} minWidth={'100px'}>
                      タイトル
                    </CmTh>
                  </CmTableHeader>
                  <CmTbody>
                    {newsList.map((news) => {
                      return (
                        <CmTableBodyRow key={news.id}>
                          <CmTd>
                            {formatCustom(
                              new Date(news.publishedAt),
                              'yyyy/MM/dd(E)HH:mm'
                            )}
                          </CmTd>
                          <CmTd>
                            {news.info_state !== '' ? (
                              <CmBox
                                color="blue.500"
                                cursor="pointer"
                                tabIndex={0}
                                onClick={() => setProductInformation(news)}
                              >
                                {news.title}
                              </CmBox>
                            ) : (
                              <a
                                target="_blank"
                                href={news.url}
                                rel="noreferrer"
                                style={{ color: '#3182ce' }}
                              >
                                <ExternalLinkIcon mr="2px" mt="-2px" />
                                {news.title}
                              </a>
                            )}
                          </CmTd>
                        </CmTableBodyRow>
                      );
                    })}
                  </CmTbody>
                </CmTable>
              </CmCard>
            </CmBox>
          </CmScrollBox>
        </CmBox>

        <CmBox width="400px">
          <CmHeading mb="20px">Camel連携可能サービス一覧</CmHeading>
          <CmScrollBox height={`calc(100vh - 155px)`} py="10px">
            <CmBox>
              <CmHeading fontSize="14px" mb="15px">
                デリバリー・テイクアウトサービス
              </CmHeading>
              <CmFlex flexWrap="wrap" justifyContent="space-between">
                <CmBox width="48%" mb="20px">
                  <img src={UberEatsImage} alt="Uber Eats" />
                </CmBox>
                <CmBox width="48%" mb="20px">
                  <img src={MenuImage} alt="menu" />
                </CmBox>
                <CmBox width="48%" mb="20px">
                  <img src={DemaecanImage} alt="Demaecan" />
                </CmBox>
              </CmFlex>
            </CmBox>
            <CmBox>
              <CmHeading fontSize="14px" mb="15px">
                モバイルオーダーサービス
              </CmHeading>
              <CmFlex flexWrap="wrap" justifyContent="space-between">
                <CmFlex
                  width="48%"
                  alignItems="center"
                  bg="white"
                  mb="20px"
                  justifyContent="center"
                >
                  <img src={RepitteImage} alt="Repitte" />
                </CmFlex>

                <CmBox bg="white" width="48%" mb="20px" padding="5px">
                  <img src={TakemeOrder} alt="TakemeOrder" />
                </CmBox>
                <CmFlex
                  alignItems="center"
                  bg="white"
                  width="48%"
                  mb="20px"
                  padding="5px"
                  height="110px"
                >
                  <img src={dokodemoChumonkunImage} alt="どこでも注文くん" />
                </CmFlex>
              </CmFlex>
            </CmBox>
            <CmBox>
              <CmHeading fontSize="14px" mb="15px">
                キッチンプリンター
              </CmHeading>
              <CmFlex flexWrap="wrap" justifyContent="space-between">
                <CmBox width="48%" mb="20px" bg="white">
                  <img src={StarImage} alt="Star" />
                </CmBox>

                <CmBox bg="white" width="48%" mb="20px" padding="5px">
                  <img src={EpsonImage} alt="Epson" />
                </CmBox>
              </CmFlex>
            </CmBox>
            <CmBox>
              <CmHeading fontSize="14px" mb="15px">
                POS/OES
              </CmHeading>
              <CmFlex flexWrap="wrap" justifyContent="space-between">
                <CmBox width="48%" mb="20px">
                  <img src={TeraokaImage} alt="TERAOKA" />
                </CmBox>
                <CmBox width="48%" mb="20px">
                  <img src={NecMobileImage} alt="TERAOKA" />
                </CmBox>
                <CmFlex
                  width="48%"
                  alignItems="center"
                  bg="white"
                  mb="20px"
                  justifyContent="center"
                >
                  <img
                    src={FoodFrontiaImage}
                    alt="FoodFrontia"
                    style={{ width: '80%' }}
                  />
                </CmFlex>
                <CmFlex
                  width="48%"
                  alignItems="center"
                  bg="white"
                  mb="20px"
                  justifyContent="center"
                >
                  <img src={SmaregiImage} alt="スマレジ" />
                </CmFlex>
              </CmFlex>
            </CmBox>
            <CmBox>
              <CmHeading fontSize="14px" mb="15px">
                店舗・販売管理システム
              </CmHeading>
              <CmFlex flexWrap="wrap" justifyContent="space-between">
                <CmFlex
                  width="48%"
                  alignItems="center"
                  bg="white"
                  mb="20px"
                  height="120px"
                  justifyContent="center"
                >
                  <img
                    src={MakasetenetexImage}
                    alt="FoodFrontia"
                    style={{ width: '80%' }}
                  />
                </CmFlex>
              </CmFlex>
            </CmBox>
          </CmScrollBox>
        </CmBox>
      </CmFlex>
      {productInformation !== undefined && (
        <ProductInformationDetailModal
          {...{
            isOpen: productInformation !== undefined,
            onClose: () => setProductInformation(undefined),
            productInformation,
          }}
        />
      )}
      {isLoading && <CmLongLoading />}
    </PageLayout>
  );
};

export default HomePage;
